/**
 * Accès React à la session (magasin externe, hors React) et actions associées.
 *
 * `useSyncExternalStore` plutôt qu'un contexte : la session est lue par le client HTTP, par les
 * gardes de route et par des composants profonds ; un état React aurait imposé de la dupliquer.
 */

import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type { OperateurInterne } from '@genviz/shared/dto';

import { api, type ErreurApi } from '../api/client';
import type { ConnexionStaffRequete, ConnexionStaffReponse } from '../api/types';
import { peut, type Capacite } from './permissions';
import {
  abonnerSession,
  delaiAvantRafraichissement,
  estConnecte,
  fermerSession,
  lireSession,
  ouvrirSession,
  rafraichirSession,
  type EtatSession,
} from './session';

/** État courant de la session, re-rendu à chaque changement. */
export function useSession(): EtatSession {
  return useSyncExternalStore(abonnerSession, lireSession, lireSession);
}

/** Opérateur connecté, `null` si la session est fermée. */
export function useOperateur(): OperateurInterne | null {
  return useSession().operateur;
}

/** Vrai si l'opérateur connecté détient la capacité demandée. */
export function usePeut(capacite: Capacite): boolean {
  const operateur = useOperateur();
  return peut(operateur, capacite);
}

/** Mutation de connexion. Le cache de requêtes est vidé pour ne rien hériter d'une session tierce. */
export function useConnexion(): UseMutationResult<
  ConnexionStaffReponse,
  ErreurApi,
  ConnexionStaffRequete
> {
  const cache = useQueryClient();
  return useMutation<ConnexionStaffReponse, ErreurApi, ConnexionStaffRequete>({
    mutationFn: (identifiants) => api.auth.connexion(identifiants),
    onSuccess: (reponse) => {
      cache.clear();
      ouvrirSession(reponse);
    },
  });
}

/** Ferme la session et purge le cache : aucune donnée client ne doit survivre à la déconnexion. */
export function useDeconnexion(): () => Promise<void> {
  const cache = useQueryClient();
  return useCallback(async () => {
    await fermerSession();
    cache.clear();
  }, [cache]);
}

/**
 * Rafraîchit le jeton d'accès avant son expiration.
 *
 * Sans cela, un opérateur qui laisse le tableau de bord ouvert verrait chaque sondage
 * automatique partir en 401 puis être rejoué : deux fois plus de requêtes, et un rafraîchissement
 * déclenché par une requête de fond au lieu d'un calendrier maîtrisé.
 */
export function useRafraichissementPreventif(): void {
  const session = useSession();

  useEffect(() => {
    if (!estConnecte()) return undefined;
    const delai = delaiAvantRafraichissement();
    if (delai === null) return undefined;

    const minuteur = window.setTimeout(() => {
      void rafraichirSession();
    }, delai);

    return () => {
      window.clearTimeout(minuteur);
    };
    // `expireA` change à chaque rafraîchissement : l'effet se replanifie de lui-même.
  }, [session.expireA, session.jetonRafraichissement]);
}
